// Lead Scout helper for Customer entity
import React from 'react';
import { toast } from 'react-hot-toast';

const LEAD_SCOUT_PROGRESS_TOAST_ID = 'customer-lead-scout-progress';

const renderProgressToast = (title, message, progress, status = 'info', customerInfo = null) => (
  <div className="w-80 max-w-sm px-4 py-3 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
    <div className="space-y-2">
      <div className="text-sm font-semibold text-gray-900 dark:text-white">{title}</div>
      {customerInfo && (
        <div className="text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 rounded px-2 py-1.5 space-y-0.5">
          <div className="font-medium truncate">{customerInfo.name}</div>
          {customerInfo.details && (
            <div className="text-gray-500 dark:text-gray-400 truncate">
              {customerInfo.details}
            </div>
          )}
        </div>
      )}
      {message && (
        <div className="text-xs text-gray-600 dark:text-gray-400 leading-snug">
          {message}
        </div>
      )}
      <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${status === 'error' ? 'bg-red-500 dark:bg-red-400' : 'bg-green-500 dark:bg-green-400'}`}
          style={{ width: `${Math.max(0, Math.min(progress, 100))}%` }}
        />
      </div>
    </div>
  </div>
);

const updateProgressToast = (progress, message, status = 'info', customerInfo = null) => {
  if (typeof window === 'undefined') return;
  toast.custom(() => renderProgressToast(
    status === 'error' ? 'Lead Scout Failed' : 'Scouting for Leads',
    message,
    progress,
    status,
    customerInfo
  ), {
    id: LEAD_SCOUT_PROGRESS_TOAST_ID,
    duration: Infinity,
    position: 'top-right'
  });
};

const dismissProgressToast = (delay = 600) => {
  if (typeof window === 'undefined') return;
  setTimeout(() => toast.dismiss(LEAD_SCOUT_PROGRESS_TOAST_ID), delay);
};

/**
 * Show confirmation dialog for keeping older content
 * @param {string} message - The confirmation message
 * @param {object} customerInfo - Customer information to display
 * @returns {Promise<boolean>} - true if user wants to keep older content, false otherwise
 */
const showConfirmationDialog = (message, customerInfo = null) => {
  return new Promise((resolve) => {
    // Create a modal dialog
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50';
    
    const customerInfoHtml = customerInfo ? `
      <div class="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
        <div class="text-sm font-medium text-gray-900 dark:text-white mb-1">
          ${customerInfo.name}
        </div>
        ${customerInfo.details ? `
          <div class="text-xs text-gray-600 dark:text-gray-400">
            ${customerInfo.details}
          </div>
        ` : ''}
      </div>
    ` : '';
    
    modal.innerHTML = `
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Summary Exceeds Character Limit
        </h3>
        ${customerInfoHtml}
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
          ${message}
        </p>
        <div class="flex gap-3 justify-end">
          <button
            id="confirm-no"
            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            No, Just Truncate
          </button>
          <button
            id="confirm-yes"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
          >
            Yes, Keep Older Content
          </button>
        </div>
      </div>
    `;
    
    document.body.appendChild(modal);
    
    const handleYes = () => {
      document.body.removeChild(modal);
      resolve(true);
    };
    
    const handleNo = () => {
      document.body.removeChild(modal);
      resolve(false);
    };
    
    modal.querySelector('#confirm-yes').addEventListener('click', handleYes);
    modal.querySelector('#confirm-no').addEventListener('click', handleNo);
    
    // Close on outside click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        handleNo();
      }
    });
  });
};

/**
 * Format customer information for display with identifying details
 * @param {object} customer - Customer object
 * @returns {object} Formatted customer info with name and details
 */
const formatCustomerInfo = (customer) => {
  if (!customer) return null;
  
  let name = '';
  let details = [];
  
  // Format name based on customer type
  if (customer.CustomerType === 'Person') {
    const nameParts = [];
    if (customer.FirstName) nameParts.push(customer.FirstName);
    if (customer.LastName) nameParts.push(customer.LastName);
    name = nameParts.join(' ').trim() || 'Unknown Person';
    
    // Add identifying details for Person
    if (customer.PersonCompanyName || customer.CompanyName) {
      details.push(`at ${customer.PersonCompanyName || customer.CompanyName}`);
    }
    if (customer.JobTitle) {
      details.push(customer.JobTitle);
    }
  } else {
    // Company customer
    name = customer.CompanyName || customer.CompanyName_Eng || 'Unknown Company';
    
    // Add branch if available
    if (customer.Branch) {
      details.push(`Branch: ${customer.Branch}`);
    }
  }
  
  // Add location information (city, country) for both types
  const location = [];
  if (customer.City) location.push(customer.City);
  if (customer.Country) location.push(customer.Country);
  if (location.length > 0) {
    details.push(location.join(', '));
  }
  
  // Add industry for companies
  if (customer.CustomerType === 'Company' && customer.Industry) {
    details.push(customer.Industry);
  }
  
  // Add customer code for additional identification
  if (customer.CustomerCode) {
    details.push(`Code: ${customer.CustomerCode}`);
  }
  
  return {
    name,
    details: details.join(' • ')
  };
};

/**
 * Perform Lead Scout for a given CustomerID.
 * Returns structured data from the backend or throws an Error with message.
 *
 * @param {number|string} customerId
 * @param {object} options - Options including languagePreference and keepOlderContent
 * @returns {Promise<any>} result.data
 */
export async function customerLeadScout(customerId, options = {}) {
  if (!customerId) {
    throw new Error('Customer ID not provided');
  }

  // Fetch customer details first to display identifying information
  let customerInfo = null;
  try {
    updateProgressToast(5, 'Loading customer information...');
    const customerResponse = await fetch(`/api/customers/${customerId}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (customerResponse.ok) {
      const customerData = await customerResponse.json();
      const customer = customerData.customer || customerData;
      customerInfo = formatCustomerInfo(customer);
    }
  } catch (err) {
    console.warn('Failed to fetch customer details for display:', err);
    // Continue without customer info display
  }

  const payload = {};
  if (options.languagePreference) {
    payload.languagePreference = options.languagePreference;
  }
  if (options.keepOlderContent !== undefined) {
    payload.keepOlderContent = options.keepOlderContent;
  }

  // Enable website search by default unless explicitly disabled
  payload.includeWebsiteSearch = options.includeWebsiteSearch !== undefined ? options.includeWebsiteSearch : true;

  updateProgressToast(15, 'Gathering recent activity...', 'info', customerInfo);

  try {
    const response = await fetch(`/api/customers/${customerId}/lead-scout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(payload)
    });

    updateProgressToast(60, 'Analyzing potential leads...', 'info', customerInfo);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Lead scout failed');
    }

    const result = await response.json();
    
    // Check if confirmation is needed
    if (result.needsConfirmation) {
      dismissProgressToast();
      updateProgressToast(80, 'Waiting for your decision...', 'info', customerInfo);
      
      // Show confirmation dialog with customer info
      const keepOlderContent = await showConfirmationDialog(
        result.message || 'The combined summary exceeds the 8000 character limit. Would you like to keep the older content? If yes, the overflow will be saved as an attachment.',
        customerInfo
      );
      
      // Retry with user's decision
      updateProgressToast(85, keepOlderContent ? 'Saving with older content...' : 'Truncating summary...', 'info', customerInfo);
      
      return await customerLeadScout(customerId, {
        ...options,
        keepOlderContent
      });
    }
    
    updateProgressToast(100, 'Lead scout complete!', 'info', customerInfo);
    dismissProgressToast();
    return result.data;
  } catch (error) {
    updateProgressToast(100, error.message || 'Lead scout failed', 'error', customerInfo);
    dismissProgressToast(2000);
    throw error;
  }
}


